import {
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import { GeneratingNameLabel } from '@src/components/common/GeneratingNameLabel';
import { InlineRenameField } from '@src/components/common/InlineRenameField';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import {
  reelSectionsSummary,
  reelSectionSummary,
  reelStructureSummary,
} from '@src/services/reel/reelStructureSummary';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import ReelIcon from '@src/svgs/ReelIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { keyframes } from 'styled-components';

import { CURSOR_ALLOWANCE_PX, TOPIC_RAIL_GUTTER_PX } from '../topicRailStyles';
import { STICKY_HEADING_ATTR } from '../utils';

import type {
  ReelTranscriptChapter,
  ReelTranscriptSection,
  ReelTranscriptStructure,
} from '@src/services/reel/reelTranscriptStructure';

/**
 * PLAT-11157 / PLAT-11158 / PLAT-11160 / PLAT-11163 — the heading rows drawn
 * between the transcript's paragraphs.
 *
 * Layout rule (review feedback): names sit FLUSH LEFT with no chevron
 * indentation, and the open/close control lives at the END of the row —
 * name · way-back · chevron. The name is the row's big click target (fold for
 * chapters, seek for sections); the chevron always folds. Every row stays a
 * flat sibling of the paragraphs, non-editable and unselectable so word
 * selection and copy read straight through them; only the controls re-enable
 * pointer events for themselves.
 */

/**
 * One shared tooltip instance for every heading row's controls, hosted by the
 * summary line above them — the one thing that renders whenever the structure
 * does. MODAL_TOOLTIP_STYLE: these rows live inside the clip-preview modal.
 */
const HEADING_TOOLTIP_ID = 'reel-transcript-heading-tooltip';

/**
 * Wide enough for the longest label — "Regenerate Name with AI and Instruction"
 * — which a 14px kebab cannot set.
 */
const MENU_WIDTH = 300;

/**
 * Why both naming items are greyed while a run is in flight, as their native
 * tooltip — the Chapter menu's own `GENERATING_TITLE_REASON`, in this view's
 * noun. Stated once: two items carry it, and a disabled control whose reason
 * differs between them reads as two different refusals.
 */
const GENERATING_TITLE_REASON = 'Already generating a name for this clip';

type ToggleProps = {
  collapsed?: boolean;
  /** Absent = the host publishes no collapse state; no chevron renders. */
  onToggle?: () => void;
};

/**
 * The fold control, at the END of the row — never in front of the name.
 * `decorative` marks the row whose NAME already carries the accessible toggle
 * (chapter): the chevron stays a pointer affordance but doesn't announce a
 * second identical control to assistive tech.
 *
 * `bold` thickens the stroke to the weight the chapter title beside it wears,
 * so the two ends of that row read at the same weight.
 */
const ChevronToggle: React.FC<{
  collapsed: boolean;
  label: string;
  onToggle: () => void;
  decorative?: boolean;
  bold?: boolean;
}> = ({ collapsed, label, onToggle, decorative = false, bold = false }) => (
  <ChevronButton
    type="button"
    aria-hidden={decorative || undefined}
    tabIndex={decorative ? -1 : undefined}
    aria-expanded={decorative ? undefined : !collapsed}
    aria-label={
      decorative ? undefined : `${collapsed ? 'Expand' : 'Collapse'} ${label}`
    }
    onClick={event => {
      event.stopPropagation();
      onToggle();
    }}
  >
    <ChevronWrap $collapsed={collapsed}>
      <ChevronDownIcon strokeWidth={bold ? '2.5' : undefined} />
    </ChevronWrap>
  </ChevronButton>
);

/**
 * What the reel adds up to, drawn where the Reel Editor draws it: under the
 * toolbar, above the prose, at that panel's own weight and wording — one line
 * over one reel, so the two tabs cannot describe it two ways. It is the reel's
 * scope, so it sits outside the scrolling prose rather than among the Chapters
 * and Sections it counts.
 */
export const ReelStructureSummaryLine: React.FC<{
  structure: ReelTranscriptStructure;
  speakersByLabel?: ReadonlyMap<string, { id: string }>;
}> = ({ structure, speakersByLabel }) => (
  <SummaryLine data-testid="reel-transcript-summary">
    {reelStructureSummary(structure, speakersByLabel)}
    {/* The heading rows' one shared tooltip instance: this line renders exactly
        when the structure does, which is when a heading can. */}
    <Tooltip id={HEADING_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />
  </SummaryLine>
);

export const ChapterHeadingRow: React.FC<
  ToggleProps & {
    chapter: ReelTranscriptChapter;
    speakersByLabel?: ReadonlyMap<string, { id: string }>;
  }
> = ({ chapter, speakersByLabel, collapsed = false, onToggle }) => {
  const title = chapter.title || `Chapter ${chapter.ordinal}`;
  // PLAT-11397 — which chapter this is, moved off the end of the summary and up
  // in front of the title. Dropped entirely when the chapter has no name of its
  // own: the fallback title IS "Chapter N", and a label over it would say the
  // same words twice.
  const label = chapter.title ? `Chapter ${chapter.ordinal}` : undefined;
  // The Reel Editor's own chapter heading: the title, and what its Sections add
  // up to on the line underneath — never pushed out to the row's right edge, so
  // a chapter reads the same on both tabs.
  const content = (
    <ChapterLines>
      {label && <ChapterLabel>{label}</ChapterLabel>}
      <ChapterName data-testid={`reel-transcript-chapter-${chapter.chapterId}`}>
        {title}
      </ChapterName>
      <ChapterSummary>
        {reelSectionsSummary(
          chapter.sections,
          chapter.duration,
          speakersByLabel,
        )}
      </ChapterSummary>
    </ChapterLines>
  );
  return (
    <ChapterRow
      contentEditable={false}
      suppressContentEditableWarning={true}
      // What the scroll math measures. A pinned heading covers the top of the
      // scroll container, so every scroll-to-a-word has to land BELOW it or the
      // word arrives invisible with the scroll reporting success (epic DE13,
      // constraint 2). The height is measured off this attribute rather than
      // assumed — see `measureStickyTopInset`.
      {...{ [STICKY_HEADING_ATTR]: 'chapter' }}
    >
      {onToggle ? (
        <NameToggle
          type="button"
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${title}`}
          onClick={event => {
            event.stopPropagation();
            onToggle();
          }}
        >
          {content}
        </NameToggle>
      ) : (
        <NameStatic>{content}</NameStatic>
      )}
      {onToggle && (
        <ChevronToggle
          collapsed={collapsed}
          label={title}
          onToggle={onToggle}
          decorative
          bold
        />
      )}
    </ChapterRow>
  );
};

/**
 * The seam a fold leaves — a Section's or a Chapter's: one quiet line where its
 * words were,
 * carrying the time the fold took off the page — so the boundary between two
 * clips stays on the page whether or not what is said in them is.
 *
 * Deliberately a row of its own rather than a number tucked onto the heading:
 * it is the place the reel gets edited between clips (inserting another clip
 * there, and more to come), and that needs somewhere to put a control.
 */
export const StructureGapRow: React.FC<{ id: string; endTime: number }> = ({
  id,
  endTime,
}) => (
  <GapRow contentEditable={false} suppressContentEditableWarning={true}>
    <GapTime data-testid={`reel-transcript-gap-${id}`}>
      {convertTimeToMMSS(endTime)}
    </GapTime>
  </GapRow>
);

export const SectionHeadingRow: React.FC<
  ToggleProps & {
    section: ReelTranscriptSection;
    speakersByLabel?: ReadonlyMap<string, { id: string }>;
    /** PLAT-11160 — this Section is under the playhead. Layout-neutral mark. */
    active?: boolean;
    /** PLAT-11160 — absent = the name is a label, not a seek control. */
    onSeek?: (section: ReelTranscriptSection) => void;
    /** PLAT-11163 — the way back: reveal this Section's row in the Reel Editor. */
    onOpenInEditor?: (section: ReelTranscriptSection) => void;
    /**
     * PLAT-11257 — the heading's own two naming actions. Both absent = no
     * kebab at all, rather than a menu with nothing in it.
     */
    onRename?: (section: ReelTranscriptSection, name: string) => void;
    /** `'auto'` runs it; `'instruct'` asks what the name should say first. */
    onGenerateName?: (
      section: ReelTranscriptSection,
      mode: 'auto' | 'instruct',
    ) => void;
    /** A generation is in flight: the items stand down and the row says so. */
    isGeneratingName?: boolean;
    /** Why the last one failed, said in the same place, until the next run. */
    nameError?: string;
  }
> = ({
  section,
  speakersByLabel,
  collapsed = false,
  onToggle,
  active = false,
  onSeek,
  onOpenInEditor,
  onRename,
  onGenerateName,
  isGeneratingName = false,
  nameError,
}) => {
  // Which of the two states the name is in. Local to the heading: only one
  // name is ever being typed, and the host has no use for the half-typed one.
  const [isRenaming, setIsRenaming] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  // Portaled and self-dismissing, the way the Reel Editor's gap menu is: these
  // rows scroll inside the transcript and the transcript is inside the clip
  // preview, so an in-place menu would be clipped by the one and stacked under
  // the other. The hook owns outside click, Escape, scroll and resize.
  const { menuRef, style: menuStyle } = useAnchoredDropdown(
    isMenuOpen,
    menuTriggerRef,
    closeMenu,
    undefined,
    { width: MENU_WIDTH, align: 'end' },
  );
  const hasMenu = !!onRename || !!onGenerateName;
  // Naming a Section that already carries a name is a *re*naming, and the menu
  // says so — "Generate" over a name that is already there reads as though the
  // menu had not noticed it. The stored name, not the display one: the opening
  // words are a fallback, and there is nothing to regenerate over them.
  const nameVerb = section.storedName ? 'Regenerate' : 'Generate';
  // The same number the Reel Editor's rows carry — a reel that uses one clip
  // several times repeats the name, and the number is what tells the uses
  // apart across the two views.
  const content = (
    <>
      <SectionOrdinal aria-hidden="true" $active={active}>
        {section.index + 1}
      </SectionOrdinal>
      <SectionMarker aria-hidden="true" $active={active} />
      <SectionName
        data-testid={`reel-transcript-section-${section.sectionId}`}
        $active={active}
      >
        {section.displayName}
      </SectionName>
    </>
  );
  return (
    <SectionRow
      contentEditable={false}
      suppressContentEditableWarning={true}
      aria-current={active || undefined}
      data-reel-section-heading={section.sectionId}
    >
      {isRenaming && onRename ? (
        // The seek control stands down while the name is being typed: the row's
        // one big target cannot be both "put the playhead here" and "edit
        // this". The ordinal and the mark stay, so the row does not jump.
        <NameStatic>
          <SectionOrdinal aria-hidden="true" $active={active}>
            {section.index + 1}
          </SectionOrdinal>
          <SectionMarker aria-hidden="true" $active={active} />
          <RenameSlot>
            <InlineRenameField
              value={section.storedName ?? ''}
              // The fallback the heading already shows. Editing opens on it,
              // and accepting it unchanged stores nothing — so it stays a
              // fallback rather than becoming a hand-typed name (DS6).
              placeholder={section.openingWords}
              ariaLabel={`Rename clip ${section.index + 1}`}
              testId={`reel-transcript-section-rename-${section.sectionId}`}
              autoEdit
              onEditingChange={editing => setIsRenaming(editing)}
              onCommit={name => onRename(section, name)}
            />
          </RenameSlot>
        </NameStatic>
      ) : onSeek ? (
        <NameToggle
          type="button"
          aria-label={`Go to ${section.displayName}`}
          data-tooltip-id={HEADING_TOOLTIP_ID}
          // What this Section is, not what the click does: the tooltip is the
          // only place its voices and runtime are stated, and "go to" was
          // saying back what a click on a Section already reads as.
          data-tooltip-content={reelSectionSummary(section, speakersByLabel)}
          onClick={event => {
            event.stopPropagation();
            onSeek(section);
          }}
        >
          {content}
        </NameToggle>
      ) : (
        <NameStatic>{content}</NameStatic>
      )}
      <EndControls>
        {/* PLAT-11257 — the run in flight, said on the row it will land on: gold
            with travelling dots, the same label the Reel Editor's rows wear. In
            line with the controls, since the name itself must not be replaced by
            a status — the creator is still reading the words under it. */}
        {isGeneratingName ? (
          <GeneratingSlot
            data-testid={`reel-transcript-section-generating-${section.sectionId}`}
          >
            <GeneratingNameLabel />
          </GeneratingSlot>
        ) : (
          // A failed run leaves the name exactly as it was, which on its own is
          // indistinguishable from a run that never started (PLAT-11059 DS4's
          // reasoning, on this surface). Truncated — the reason is whatever the
          // endpoint said — with the full text on the title attribute.
          nameError && (
            <NameErrorSlot
              data-testid={`reel-transcript-section-name-error-${section.sectionId}`}
              title={nameError}
            >
              {nameError}
            </NameErrorSlot>
          )
        )}
        {onOpenInEditor && (
          <OpenInEditorButton
            type="button"
            aria-label={`Open ${section.displayName} in Reel Editor`}
            data-tooltip-id={HEADING_TOOLTIP_ID}
            data-tooltip-content="Open in Reel Editor"
            // Above the row, right-aligned to the glyph — NOT plain `top`.
            // react-tooltip's flip middleware checks the cross axis, and a
            // centred tooltip on a 16px glyph this close to the panel's edge
            // overflows to the right; flip then walks past `bottom` to the
            // cross-axis fallback and opens the tooltip to the LEFT, over the
            // Section name. `top-end` puts its right edge on the glyph's, so
            // there is no overflow to flip away from.
            data-tooltip-place="top-end"
            onClick={event => {
              event.stopPropagation();
              onOpenInEditor(section);
            }}
          >
            {/* The Reel Editor tab's own glyph, from the strip above — this
                control goes to that tab, so it wears that tab's icon rather
                than a second reel drawing of its own. */}
            <ReelIcon width="16" height="16" />
          </OpenInEditorButton>
        )}
        {hasMenu && (
          <HeadingMenuButton
            ref={menuTriggerRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            aria-label={`${section.displayName} actions`}
            data-testid={`reel-transcript-section-menu-button-${section.sectionId}`}
            data-tooltip-id={HEADING_TOOLTIP_ID}
            // Nothing while the menu is open: the menu names its own actions in
            // more words than a tooltip has (the gap menu's reasoning).
            data-tooltip-content={isMenuOpen ? '' : 'Name this clip'}
            data-tooltip-place="top-end"
            onClick={event => {
              event.stopPropagation();
              setIsMenuOpen(open => !open);
            }}
          >
            <MoreVertIcon width="14" height="14" />
          </HeadingMenuButton>
        )}
        {onToggle && (
          <ChevronToggle
            collapsed={collapsed}
            label={section.displayName}
            onToggle={onToggle}
            bold
          />
        )}
      </EndControls>
      {isMenuOpen &&
        menuStyle &&
        createPortal(
          <ContextMenuSurface
            ref={menuRef}
            style={menuStyle}
            role="menu"
            data-testid={`reel-transcript-section-menu-${section.sectionId}`}
          >
            {onRename && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                data-testid={`reel-transcript-section-rename-item-${section.sectionId}`}
                onClick={() => {
                  closeMenu();
                  setIsRenaming(true);
                }}
              >
                <SimplePencilIcon width="14" height="14" />
                Rename
              </ContextMenuItem>
            )}
            {onGenerateName && (
              <>
                <ContextMenuItem
                  type="button"
                  role="menuitem"
                  disabled={isGeneratingName}
                  title={isGeneratingName ? GENERATING_TITLE_REASON : undefined}
                  data-testid={`reel-transcript-section-generate-item-${section.sectionId}`}
                  onClick={() => {
                    if (isGeneratingName) return;
                    closeMenu();
                    onGenerateName(section, 'auto');
                  }}
                >
                  {/* The Reel Editor's own naming item, glyph and words: the
                      same action on the same Section, reached from the other
                      tab — including `currentColor`, so the sparkle reads as
                      part of the item's label rather than as its own green
                      control. */}
                  <SparkleIcon
                    width="14"
                    height="14"
                    strokeColor="currentColor"
                  />
                  {nameVerb} Name with AI
                </ContextMenuItem>
                {/* The same run, steered. Its own item rather than a modifier on
                    the one above: a creator who wants to say what the name
                    should say should not have to learn that this menu asks
                    sometimes and not others. */}
                <ContextMenuItem
                  type="button"
                  role="menuitem"
                  disabled={isGeneratingName}
                  title={isGeneratingName ? GENERATING_TITLE_REASON : undefined}
                  data-testid={`reel-transcript-section-instruct-item-${section.sectionId}`}
                  onClick={() => {
                    if (isGeneratingName) return;
                    closeMenu();
                    onGenerateName(section, 'instruct');
                  }}
                >
                  <SparkleIcon
                    width="14"
                    height="14"
                    strokeColor="currentColor"
                  />
                  {nameVerb} Name with AI and Instruction
                </ContextMenuItem>
              </>
            )}
          </ContextMenuSurface>,
          document.body,
        )}
    </SectionRow>
  );
};

/* Shared row chrome: flex line, name flush left and winning the width fight,
   controls clustered at the end, selection passes over everything. */

const HeadingRowBase = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  pointer-events: none;
`;

/**
 * The name is the row's big click target and takes every pixel the meta and
 * controls leave over — a long reel or clip title truncates against the
 * meta, never against a phantom constraint.
 */
const NameToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 2px 6px;
  margin-left: -6px;
  border: none;
  border-radius: 4px;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  pointer-events: auto;
  cursor: pointer;
  transition: background-color var(--transition-fast);

  &:hover {
    background: var(--surface-elevated);
  }
`;

const NameStatic = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const ChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: none;
  color: var(--text-muted);
  pointer-events: auto;
  cursor: pointer;
  transition: background-color var(--transition-fast);

  &:hover {
    background: var(--surface-elevated);
  }
`;

const ChevronWrap = styled.span<{ $collapsed: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  transform: rotate(${({ $collapsed }) => ($collapsed ? '-90deg' : '0deg')});
  transition: transform var(--transition-fast);
`;

/**
 * The three controls at the row's end, as one cluster: the way back to the Reel
 * Editor, the naming menu, the fold.
 *
 * Their own tight gap rather than the row's 8px, which is the space between the
 * *name* and the controls — at that width three icon buttons read as three
 * separate things stranded at the end of the row instead of one set of controls
 * belonging to it.
 */
const EndControls = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-left: auto;
`;

/**
 * The in-flight label's place in that cluster: at the row's own small scale,
 * with air before the controls so the phrase does not touch the reel glyph.
 * `role="status"` so the run is announced once, here — the name area keeps the
 * name, which is the point of putting the label out at the end.
 */
const GeneratingSlot = styled.span.attrs({ role: 'status' })`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 4px;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

/**
 * The failure, in the slot the in-flight label just left. Capped rather than
 * given the row: the message is the endpoint's, and a long one would push the
 * controls off the end of the heading.
 */
const NameErrorSlot = styled(GeneratingSlot)`
  max-width: 40%;
  color: var(--destructive);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
`;

/**
 * PLAT-11257 — the heading's own actions, in the row's end cluster between the
 * way back and the chevron: the naming a Section carries is the Section's, so
 * it opens where the Section is rather than only on the other tab.
 *
 * The shared kebab, so a clip's actions open from the same glyph in the same
 * place they do in the Reel Editor (`docs/design-system.md`, "Context / Action
 * Menus"). Always visible, unlike the Reel Editor row's hover-revealed one:
 * these rows sit in prose the creator is reading, where a control that appears
 * on hover is a control they have to go looking for.
 */
const HeadingMenuButton = styled(KebabButton)`
  margin-left: 0;
  padding: 2px;
  pointer-events: auto;

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

/**
 * The name's editing state. It re-enables the pointer and text selection the
 * heading row turns off for the prose around it — the row is inert so that a
 * drag over the transcript selects words straight through it, but an input the
 * creator cannot click into or select inside is not an input.
 */
const RenameSlot = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  pointer-events: auto;
  user-select: text;
`;

/**
 * PLAT-11163 — the one navigation control that leaves the transcript. Quiet at
 * rest (muted glyph); sits between the meta and the chevron in the row's end
 * cluster.
 *
 * Hover is the Reel Editor tab's own, since this is that tab's glyph and it
 * goes to that tab: the elevated fill and the glyph to full white.
 */
const OpenInEditorButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: none;
  color: var(--text-muted);
  pointer-events: auto;
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    color var(--transition-fast);

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

/**
 * The same tokens the Reel Editor's summary wears (its `Subtitle`), so the line
 * reads identically on both tabs.
 *
 * The inset is the transcript text's own left edge, rebuilt: this line renders
 * in the header band, a sibling ABOVE the scroll container, so it inherits
 * neither the scroller's rail gutter nor the cursor allowance the prose carries
 * inside its wrapper. Without both it sits left of the Chapter titles it
 * summarises.
 */
const SummaryLine = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  font-variant-numeric: tabular-nums;
  padding-left: ${TOPIC_RAIL_GUTTER_PX + CURSOR_ALLOWANCE_PX}px;
  /* Up under the toolbar: it reads as that row's caption, not as a band of its
     own between the controls and the prose. */
  margin-top: -4px;
`;

/**
 * The one heading that stays: a chapter holds the prose under it, so while the
 * creator reads inside it its title rides the top of the scroller and the next
 * chapter's title slides up and takes the place over as it arrives.
 *
 * Plain overlap rather than a push-off — every row is a flat sibling of the
 * paragraphs (the DOM the transcript's text selection needs), so the chapters
 * share one containing block and each stuck title simply covers the last. That
 * is why the row has to paint: `--background` is the modal Content's own fill,
 * so the words pass under it rather than through it. The bottom margin becomes
 * padding for the same reason — margins do not paint, and the line arriving
 * from below would otherwise touch the title.
 *
 * The band is wider than the row's content on purpose: a Section heading is a
 * card that bleeds 6px past the prose on both sides, so a band only as wide as
 * the text would let the grey corners slide out either end of it. Negative
 * margins take the paint out that far and the matching padding puts the title
 * back where it was.
 */
const ChapterRow = styled(HeadingRowBase)`
  position: sticky;
  top: 0;
  /* Over the prose AND over the Section cards passing under it: both the words
     and those cards paint later in document order, so without a stacking order
     of its own the header would end up behind them. */
  z-index: 2;
  margin: 18px -6px 4px;
  padding: 6px;
  /* PLAT-11397 — the geometry a chapter is told apart BY, now that the fill
     cannot do it alone: bottom corners only, under a hairline. A rule with a
     band hanging off it reads as the boundary the clips below are filed under;
     the Section cards keep the fully rounded box, so the two stop being the
     same shape in two greys. The bleed stays -6px either side — the scroller
     computes overflow-x: auto and clips at gutter + cursor allowance (see
     topicRailCss), and that gutter is conditional, so a wider band would be
     shaved off on any surface that does not reserve it. */
  border-radius: 0 0 6px 6px;
  border-top: 1px solid var(--border-subtle);
  background: var(--surface-elevated);
  /* A ring of page background around the card. While the row is stuck the
     prose runs UNDER it, and a card that only paints its own box would let the
     words show through the margins either side of it. */
  box-shadow: 0 0 0 6px var(--background);

  /* The name's own hover, a step up from the band under it. The shared
     NameToggle hover is --surface-elevated, which is this row's resting fill —
     on a chapter it painted nothing at all. */
  ${NameToggle}:hover {
    background: var(--surface-elevated-hover);
  }
`;

/**
 * PLAT-11397 — which chapter this is, above its name. The band has to say what
 * kind of row it is before the creator reads the words on it, which is what the
 * ordinal already does for a clip card; on the end of the summary it was the
 * last thing read rather than the first.
 */
const ChapterLabel = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  font-weight: var(--font-weight-semi-bold);
  font-variant-numeric: tabular-nums;
`;

/** The Reel Editor chapter row's `Lines`: the title over its summary. */
const ChapterLines = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ChapterName = styled.span`
  /* PLAT-11397 — a step above the clip names below it in colour and weight, and
     half a step in size.

     OFF-SCALE, deliberately and knowingly: 0.9375rem is 15px, which sits
     between --text-sm (14px) and --text-md (16px). The scale has no step there.
     --text-md made the title compete with the 16px prose it heads; --text-sm
     left it the same size as the clip names under it. If this size sticks it
     wants promoting to a token in theme.css rather than staying a literal
     here. */
  color: var(--text-primary);
  font-size: 0.9375rem;
  line-height: var(--leading-text-sm);
  font-weight: var(--font-weight-semi-bold);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

/**
 * The Reel Editor chapter row's `Count`, tokens and all: its own scale's
 * leading rather than the inherited body one, which would add a couple of
 * pixels of air under the title for nothing.
 */
const ChapterSummary = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * The folded Section's seam: the boundary time alone, at the end of the row, in
 * the register the transcript's own timestamps wear. Quiet on purpose — it
 * marks a place, it does not announce one. The row keeps its full width so the
 * controls it is going to grow have somewhere to land.
 */
const GapRow = styled(HeadingRowBase)`
  margin: 4px 0;
`;

/**
 * PLAT-11163 — the landing flash: the Reel Editor's arrival token (its
 * `revealFade`), so both ends of the round trip mark the row the same way.
 *
 * A CSS animation, deliberately, and not `element.animate()`: WAAPI keyframes
 * do not substitute `var()`, so a design token handed to it is dropped as an
 * invalid value and the flash never paints. The jump sets
 * {@link LANDING_FLASH_ATTR} on the row and clears it when the animation ends.
 */
const landingFade = keyframes`
  from {
    background-color: var(--accent-tint-strong);
  }
  to {
    background-color: var(--surface);
  }
`;

/** How long the landing flash runs. The jump clears the attribute after it. */
export const LANDING_FLASH_MS = 2000;

/** Set by the jump on the row it landed on; removed when the flash is done. */
export const LANDING_FLASH_ATTR = 'data-reel-landing-flash';

const GapTime = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
`;

/**
 * The one heading that wears a surface of its own, always — not only under the
 * pointer. It runs the full width of the row, past the way-back icon and the
 * chevron, so a Section reads as a card the prose is filed under rather than as
 * a line of text among the words.
 *
 * The row carries the inset the name button used to hold (its `-6px`), so the
 * ordinal still starts on the prose's left edge with the fill behind it.
 */
const SectionRow = styled(HeadingRowBase)`
  margin: 12px -6px 2px;

  /* After a folded Section's seam the row's usual 12px would sit the time hard
     against the heading below it and leave twice the air under as over. Margins
     collapse between these siblings, so this is what the space below the time
     actually is — matched to the 4px above it. */
  ${GapRow} + & {
    margin-top: 4px;
  }
  padding: 4px 6px;
  border-radius: 6px;
  /* PLAT-11397 — --surface, the token the depth scale assigns to card and item
     backgrounds, which is what a Section row is: a selectable item. It used to
     wear --surface-elevated, the chapter band's own fill, so the two rows were
     the same card in the same grey and the column read as one repeating
     object. One step down puts the band above the cards filed under it, which
     is the order they actually stand in. */
  background: var(--surface);

  /* The row is the surface now: the name neither insets itself again nor
     paints a second fill on top of this one. Its hover goes a step lighter
     instead, so the seek affordance still answers the pointer.

     No left padding either — the row's own 6px is the whole inset, and a
     second one here is what used to push the ordinal off the prose's left
     edge. The hover pill starts on the number instead. */
  ${NameToggle} {
    margin-left: 0;
    padding-left: 0;

    &:hover {
      background: var(--surface-elevated);
    }
  }

  &[${LANDING_FLASH_ATTR}] {
    animation: ${landingFade} ${LANDING_FLASH_MS}ms ease-out;
  }
`;

/**
 * The Reel Editor's row number, so repeated clip names stay tellable apart. It
 * turns gold with the mark beside it — the two are one marking, so they carry
 * the playhead together rather than one of them wearing it alone.
 */
const SectionOrdinal = styled.span<{ $active?: boolean }>`
  flex-shrink: 0;
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-muted)')};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
  transition: color var(--transition-fast);
`;

/**
 * PLAT-11160 — the playhead mark. Color only, never size: the marking must
 * not move a single pixel of layout, so auto-scroll has nothing to fight.
 *
 * Gold on the Section under the playhead, the same muted grey as the ordinal
 * everywhere else — a dimmed gold read as a half-lit marking on every row.
 */
const SectionMarker = styled.span<{ $active?: boolean }>`
  flex-shrink: 0;
  width: 3px;
  height: 12px;
  border-radius: 2px;
  background: ${({ $active }) =>
    $active ? 'var(--accent)' : 'var(--text-muted)'};
  transition: background-color var(--transition-fast);
`;

const SectionName = styled.span<{ $active?: boolean }>`
  color: ${({ $active }) =>
    $active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  /* A step up from the meta around it: the Section name is what the creator
     reads down the column, and on its own surface it can carry the size. */
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  transition: color var(--transition-fast);
`;
